import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useApplicationSettings, ApplicationSettings } from '../modules/admin/settings/useData';
import { APP_CONFIG } from '../config/appConfig';
import { APP_UI_DEFAULTS } from '../config/appUiDefaults';
import { assertFrontendAppUiDefaults } from '../config/assertFrontendAppUiDefaults';
import { updateApiTimeout } from '../services/api';

assertFrontendAppUiDefaults();

interface MergedAppConfig {
  // Date & Time Formatting (from appConfig.ts)
  dateFormat: string;
  dateTimeFormat: string;
  timeFormat: string;
  apiDateFormat: string;
  apiDateTimeFormat: string;
  
  // Locale Settings (from appConfig.ts)
  dateLocale: string;
  dateLocaleOptions: {
    day: '2-digit';
    month: '2-digit';
    year: 'numeric';
  };
  
  // Table Configuration (merged with ApplicationSettings)
  table: {
    defaultPageSize: number;
    pageSizeOptions: number[];
    defaultDensity: 'compact' | 'normal' | 'comfortable';
    densityConfig: typeof APP_CONFIG.table.densityConfig;
    densityOptions: typeof APP_CONFIG.table.densityOptions;
    dateColumnWidth: string;
    dateTimeColumnWidth: string;
  };
  
  // API Settings (from ApplicationSettings)
  api: {
    timeout: number;
    retryAttempts: number;
    retryDelay: number;
    cacheDuration: number;
  };
  
  // Feature Flags (from ApplicationSettings)
  features: {
    enableAdvancedSearch: boolean;
    enableExport: boolean;
    enableDarkMode: boolean;
    enableTablePresets: boolean;
    enableConditionalFormatting: boolean;
    enableFooter: boolean;
  };

  // Layout (from ApplicationSettings)
  layout: {
    sidebarStyle: 'classic' | 'rail' | 'tabs' | 'workspaces' | 'topmenu' | 'sidebar_topmenu';
  };

  // Dashboard grid (from ApplicationSettings)
  dashboard: {
    gridColumns: number;
    statCardWidth: number;
    listWidgetWidth: number;
    chartWidth: number;
    fullWidth: number;
  };
  
  // System Settings (merged with ApplicationSettings)
  system: {
    appName: string;
    version: string;
    environment: string;
    debugMode: boolean;
    vesselAppUrl: string;
  };
}

interface SettingsContextType {
  applicationSettings: ApplicationSettings | undefined;
  isLoading: boolean;
  isError: any;
  appConfig: MergedAppConfig;
  refreshSettings: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Only fetch settings when user is authenticated (handled by SWR)
  const { applicationSettings, isLoading, isError, mutate } = useApplicationSettings();

  // Merge ApplicationSettings with APP_CONFIG defaults
  const appConfig: MergedAppConfig = React.useMemo(() => {
    const defaults = {
      // Use database settings if available, otherwise fallback to APP_CONFIG
      table: {
        defaultPageSize: applicationSettings?.defaultTablePageSize ?? APP_CONFIG.table.defaultPageSize,
        pageSizeOptions: applicationSettings?.pageSizeOptions ?? APP_CONFIG.table.pageSizeOptions,
        defaultDensity: (applicationSettings?.defaultTableDensity ?? APP_CONFIG.table.defaultDensity) as 'compact' | 'normal' | 'comfortable',
        densityConfig: APP_CONFIG.table.densityConfig,
        densityOptions: APP_CONFIG.table.densityOptions,
        dateColumnWidth: APP_CONFIG.table.dateColumnWidth,
        dateTimeColumnWidth: APP_CONFIG.table.dateTimeColumnWidth,
      },
      api: {
        timeout: applicationSettings?.apiTimeout ?? APP_CONFIG.api.timeout,
        retryAttempts: APP_CONFIG.api.retryAttempts,
        retryDelay: APP_CONFIG.api.retryDelay,
        cacheDuration: APP_CONFIG.api.cacheDuration,
      },
      features: {
        enableAdvancedSearch: applicationSettings?.enableAdvancedSearch ?? APP_CONFIG.features.enableAdvancedSearch,
        enableExport: applicationSettings?.enableExport ?? APP_CONFIG.features.enableExport,
        enableDarkMode: applicationSettings?.enableDarkMode ?? APP_CONFIG.features.enableDarkMode,
        enableTablePresets: applicationSettings?.enableTablePresets ?? APP_CONFIG.features.enableTablePresets,
        enableConditionalFormatting: applicationSettings?.enableConditionalFormatting ?? APP_CONFIG.features.enableConditionalFormatting,
        enableFooter: applicationSettings?.enableFooter ?? APP_CONFIG.features.enableFooter,
      },
      layout: {
        sidebarStyle: (applicationSettings?.sidebarStyle as MergedAppConfig['layout']['sidebarStyle']) ?? APP_UI_DEFAULTS.navigation.sidebarStyle,
      },
      dashboard: {
        gridColumns: applicationSettings?.dashboardGridColumns ?? APP_CONFIG.dashboard.gridColumns,
        statCardWidth: applicationSettings?.dashboardStatCardWidth ?? APP_CONFIG.dashboard.statCardWidth,
        listWidgetWidth: applicationSettings?.dashboardListWidgetWidth ?? APP_CONFIG.dashboard.listWidgetWidth,
        chartWidth: applicationSettings?.dashboardChartWidth ?? APP_CONFIG.dashboard.chartWidth,
        fullWidth: applicationSettings?.dashboardFullWidth ?? APP_CONFIG.dashboard.fullWidth,
      },
      system: {
        appName: applicationSettings?.appName ?? APP_CONFIG.system.appName,
        version: APP_CONFIG.system.version,
        environment: APP_CONFIG.system.environment,
        debugMode: APP_CONFIG.system.debugMode,
        vesselAppUrl: (
          (typeof applicationSettings?.['general.vesselAppUrl'] === 'string'
            ? applicationSettings['general.vesselAppUrl']
            : '') ||
          process.env.REACT_APP_VESSEL_APP_URL ||
          ''
        ).replace(/\/$/, ''),
      },
    };

    return {
      dateFormat: APP_CONFIG.dateFormat,
      dateTimeFormat: APP_CONFIG.dateTimeFormat,
      timeFormat: APP_CONFIG.timeFormat,
      apiDateFormat: APP_CONFIG.apiDateFormat,
      apiDateTimeFormat: APP_CONFIG.apiDateTimeFormat,
      dateLocale: APP_CONFIG.dateLocale,
      dateLocaleOptions: APP_CONFIG.dateLocaleOptions,
      ...defaults,
    };
  }, [applicationSettings]);

  const refreshSettings = () => {
    mutate();
  };

  // Update API timeout when settings change
  useEffect(() => {
    if (appConfig.api.timeout) {
      updateApiTimeout(appConfig.api.timeout);
    }
  }, [appConfig.api.timeout]);

  return (
    <SettingsContext.Provider
      value={{
        applicationSettings,
        isLoading,
        isError,
        appConfig,
        refreshSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

